<!-- .../builder/canvas/TextToolbar.vue -->
<script setup>
// Barra flutuante do texto: formatação, link, cor, tamanho, lista e variáveis.
import { ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { VARIABLES } from './richSchema';

defineProps({ inline: { type: Boolean, default: false } });
const emit = defineEmits(['command', 'done']);
const { t } = useI18n();
const linkOpen = ref(false);
const linkValue = ref('https://');
const varsOpen = ref(false);
const SIZES = [12, 13, 14, 15, 16, 18, 20, 24, 28, 32, 40];
const BUTTONS = [
  ['bold', 'i-lucide-bold', 'BOLD'],
  ['italic', 'i-lucide-italic', 'ITALIC'],
  ['underline', 'i-lucide-underline', 'UNDERLINE'],
];

const applyLink = () => {
  emit('command', 'link', linkValue.value.trim());
  linkOpen.value = false;
};
const pickSize = event => {
  const { target } = event;
  emit('command', 'size', `${target.value}px`);
  target.value = '';
};
const token = name => `{{${name}}}`;
const pickVariable = name => {
  emit('command', 'variable', name);
  varsOpen.value = false;
};
</script>

<template>
  <div
    class="flex items-center gap-0.5 rounded-lg border border-n-weak bg-n-solid-1 p-1 shadow-lg"
    @mousedown.prevent
  >
    <button
      v-for="[cmd, icon, label] in BUTTONS"
      :key="cmd"
      type="button"
      class="rounded p-1 text-n-slate-12 hover:bg-n-solid-3"
      :title="t(`EMAIL_TEMPLATES.EDITOR.TEXT.${label}`)"
      @click="emit('command', cmd)"
    >
      <span :class="icon" class="block size-4" />
    </button>
    <div class="relative">
      <button
        type="button"
        class="rounded p-1 text-n-slate-12 hover:bg-n-solid-3"
        :title="t('EMAIL_TEMPLATES.EDITOR.TEXT.LINK')"
        @click="linkOpen = !linkOpen"
      >
        <span class="i-lucide-link block size-4" />
      </button>
      <div
        v-if="linkOpen"
        class="absolute left-0 top-full z-40 mt-1 flex w-72 gap-1 rounded-lg border border-n-weak bg-n-solid-1 p-2 shadow-lg"
        @mousedown.stop
      >
        <input
          v-model="linkValue"
          class="flex-1 rounded-md border border-n-weak bg-transparent px-2 py-1 text-xs"
          :placeholder="t('EMAIL_TEMPLATES.EDITOR.TEXT.LINK_PROMPT')"
          @keydown.enter.prevent="applyLink"
        />
        <button
          type="button"
          class="rounded-md bg-n-brand px-2 text-xs text-white"
          @click="applyLink"
        >
          {{ t('EMAIL_TEMPLATES.EDITOR.TEXT.DONE') }}
        </button>
        <button
          type="button"
          class="rounded-md px-1 text-xs text-n-slate-11"
          :title="t('EMAIL_TEMPLATES.EDITOR.TEXT.UNLINK')"
          @click="
            emit('command', 'link', '');
            linkOpen = false;
          "
        >
          <span class="i-lucide-unlink block size-4" />
        </button>
      </div>
    </div>
    <label
      class="relative rounded p-1 hover:bg-n-solid-3"
      :title="t('EMAIL_TEMPLATES.EDITOR.TEXT.COLOR')"
    >
      <span class="i-lucide-palette block size-4 text-n-slate-12" />
      <input
        type="color"
        class="absolute inset-0 cursor-pointer opacity-0"
        @input="emit('command', 'color', $event.target.value)"
      />
    </label>
    <select
      class="rounded border-0 bg-transparent py-0.5 text-xs"
      :title="t('EMAIL_TEMPLATES.EDITOR.TEXT.SIZE')"
      @mousedown.stop
      @change="pickSize($event)"
    >
      <option value="" disabled selected>
        {{ t('EMAIL_TEMPLATES.EDITOR.TEXT.SIZE') }}
      </option>
      <option v-for="s in SIZES" :key="s" :value="s">{{ s }}</option>
    </select>
    <template v-if="!inline">
      <button
        type="button"
        class="rounded p-1 text-n-slate-12 hover:bg-n-solid-3"
        :title="t('EMAIL_TEMPLATES.EDITOR.TEXT.LIST')"
        @click="emit('command', 'list')"
      >
        <span class="i-lucide-list block size-4" />
      </button>
      <button
        type="button"
        class="rounded p-1 text-n-slate-12 hover:bg-n-solid-3"
        :title="t('EMAIL_TEMPLATES.EDITOR.TEXT.ORDERED')"
        @click="emit('command', 'ordered')"
      >
        <span class="i-lucide-list-ordered block size-4" />
      </button>
    </template>
    <div class="relative">
      <button
        type="button"
        class="flex items-center gap-1 rounded px-1.5 py-1 text-xs text-n-slate-12 hover:bg-n-solid-3"
        @click="varsOpen = !varsOpen"
      >
        <span class="i-lucide-braces block size-4" />
        {{ t('EMAIL_TEMPLATES.EDITOR.TEXT.VARIABLE') }}
      </button>
      <div
        v-if="varsOpen"
        class="absolute right-0 top-full z-40 mt-1 w-56 rounded-lg border border-n-weak bg-n-solid-1 p-1 shadow-lg"
      >
        <button
          v-for="v in VARIABLES"
          :key="v"
          type="button"
          class="flex w-full justify-between rounded px-2 py-1 text-left text-xs hover:bg-n-solid-3"
          @click="pickVariable(v)"
        >
          <span>{{ t(`EMAIL_TEMPLATES.EDITOR.VARIABLES.${v}`) }}</span>
          <code class="text-n-slate-10">{{ token(v) }}</code>
        </button>
      </div>
    </div>
    <button
      type="button"
      class="ml-1 rounded-md bg-n-brand px-2 py-1 text-xs text-white"
      @click="emit('done')"
    >
      {{ t('EMAIL_TEMPLATES.EDITOR.TEXT.DONE') }}
    </button>
  </div>
</template>
